import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from '@quintoandar/cozy-core/Box';
import Button from '@quintoandar/cozy-core/Button';
import IconButton from '@quintoandar/cozy-core/IconButton';
import Popover from '@quintoandar/cozy-core/Popover';
import Tag from '@quintoandar/cozy-core/Tag';
import Typography from '@quintoandar/cozy-core/Typography';
import Info from '@quintoandar/cozy-icons/Info';

import messages from './messages';
import { getDaysLeftToReschedule } from './rescheduleDeadline';

// Keeps clicks out of the card's onClick, including the popover's (React events bubble through portals).
const stopPropagation = (event) => event.stopPropagation();

export const buildDeadlineTag = ({ intl, rescheduleDeadline, currentDate }) => {
  if (!rescheduleDeadline) {
    return { kind: 'warning', label: intl.formatMessage(messages.problemReported) };
  }

  const days = getDaysLeftToReschedule({ deadline: rescheduleDeadline, currentDate });
  if (days === 1) {
    return { kind: 'critical', label: intl.formatMessage(messages.rescheduleLastDay) };
  }

  return { kind: 'warning', label: intl.formatMessage(messages.rescheduleDaysLeft, { days }) };
};

const ProblemSessionNotice = ({ rescheduleDeadline, onReschedule }) => {
  const intl = useIntl();
  const [infoAnchor, setInfoAnchor] = useState(null);

  const openInfo = (event) => setInfoAnchor(event.currentTarget);
  const closeInfo = () => setInfoAnchor(null);

  const deadlineTag = buildDeadlineTag({ intl, rescheduleDeadline, currentDate: new Date() });

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={stopPropagation}>
      <Box mt={1} display="flex" alignItems="center">
        <Tag kind={deadlineTag.kind} label={deadlineTag.label} />
        <Box ml={1}>
          <IconButton
            icon={Info}
            size="small"
            aria-label={intl.formatMessage(messages.rescheduleInfoAriaLabel)}
            onClick={openInfo}
          />
        </Box>
      </Box>

      <Popover
        open={!!infoAnchor}
        anchorEl={infoAnchor}
        onClose={closeInfo}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={2} maxWidth={280}>
          <Typography level="body">
            <FormattedMessage {...messages.rescheduleInfo} />
          </Typography>
        </Box>
      </Popover>

      <Box mt={2}>
        <Button onClick={onReschedule} fullWidth>
          <FormattedMessage {...messages.rescheduleButton} />
        </Button>
      </Box>
    </div>
  );
};

ProblemSessionNotice.propTypes = {
  rescheduleDeadline: PropTypes.instanceOf(Date),
  onReschedule: PropTypes.func.isRequired,
};

export default ProblemSessionNotice;
